import { useCallback, useMemo, useState } from 'react';

import {
  ExtendedOrganisationListItem,
  ExtendedWorkspaceListItem
} from '@futrli/views/operations/workspaces/processors/workspacesList';
import { OrganisationDSDisplayStatus } from '@futrli/views/operations/workspaces/types';
import { OrganisationListItem } from '@futrli-sdk/workspaces';
import { readPersistedFilters, useListFilterPersistence } from '@futrli/predict/utils/list-filters';

const WORKSPACES_STORAGE_KEY = 'futrli:workspaces-list-filters';
const WORKSPACE_STORAGE_KEY = 'futrli:workspace-list-filters';

interface WorkspaceListFilters {
  workspaceFilterValue: string;
  datasourceFilterValue: string;
  statusFilterValue: string;
}

const matchesSearch = (organisation: OrganisationListItem, searchTerm: string): boolean => {
  if (!searchTerm) {
    return true;
  }
  return organisation.name.toLowerCase().includes(searchTerm.toLowerCase());
};

const matchesWorkspace = (workspace: ExtendedWorkspaceListItem, workspaceFilterValue: string): boolean => {
  if (workspaceFilterValue === 'all') {
    return true;
  }
  return workspace.id === workspaceFilterValue;
};

const getDatasourceFilterValue = (organisation: ExtendedOrganisationListItem): string | undefined => {
  return organisation.integration?.slug;
};

const matchesDatasource = (organisation: ExtendedOrganisationListItem, datasourceFilterValue: string): boolean => {
  if (datasourceFilterValue === 'all') {
    return true;
  }

  return getDatasourceFilterValue(organisation) === datasourceFilterValue;
};

const matchesStatus = (organisation: ExtendedOrganisationListItem, statusFilterValue: string): boolean => {
  if (statusFilterValue === 'all') {
    return true;
  }

  return organisation.datasourceDisplayStatus === (statusFilterValue as OrganisationDSDisplayStatus);
};

export const useWorkspacesFilters = (workspaces: ExtendedWorkspaceListItem[], workspacePage = false) => {
  const [searchTerm, setSearchTerm] = useState('');
  const STORAGE_KEY = workspacePage ? WORKSPACE_STORAGE_KEY : WORKSPACES_STORAGE_KEY;
  const persisted = readPersistedFilters(STORAGE_KEY, '');

  const [workspaceFilterValue, setWorkspaceFilterValue] = useState(() => {
    return (persisted?.filters?.workspaceFilterValue as string) ?? 'all';
  });
  const [datasourceFilterValue, setDatasourceFilterValue] = useState(() => {
    return (persisted?.filters?.datasourceFilterValue as string) ?? 'all';
  });
  const [statusFilterValue, setStatusFilterValue] = useState(() => {
    return (persisted?.filters?.statusFilterValue as string) ?? 'all';
  });

  const onSearchChange = useCallback((value: string) => setSearchTerm(value), []);
  const onWorkspaceFilterChange = useCallback((value: string) => setWorkspaceFilterValue(value), []);
  const onDatasourceFilterChange = useCallback((value: string) => setDatasourceFilterValue(value), []);
  const onStatusFilterChange = useCallback((value: string) => setStatusFilterValue(value), []);

  const filters = useMemo<WorkspaceListFilters>(
    () => ({ workspaceFilterValue, datasourceFilterValue, statusFilterValue }),
    [workspaceFilterValue, datasourceFilterValue, statusFilterValue]
  );

  const handleRestore = useCallback((restoredFilters: WorkspaceListFilters) => {
    setWorkspaceFilterValue(restoredFilters.workspaceFilterValue);
    setDatasourceFilterValue(restoredFilters.datasourceFilterValue ?? 'all');
    setStatusFilterValue(restoredFilters.statusFilterValue ?? 'all');
  }, []);

  useListFilterPersistence({
    storageKey: STORAGE_KEY,
    organisationId: '',
    filters,
    sort: undefined,
    onRestore: handleRestore
  });

  const filteredWorkspaces = useMemo(() => {
    // Filters orgs within workspaces based on search term
    const filteredWorkspaces = workspaces.map(workspace => ({
      ...workspace,
      organisations: workspace.organisations.filter(
        organisation =>
          matchesSearch(organisation, searchTerm) &&
          matchesWorkspace(workspace, workspaceFilterValue) &&
          matchesDatasource(organisation, datasourceFilterValue) &&
          matchesStatus(organisation, statusFilterValue)
      )
    }));

    // Remove any workspaces with 0 organisations after filtering
    const filteredWorkspacesWithOrganisations = () =>
      filteredWorkspaces.filter(workspace => workspace.organisations.length > 0);

    return filteredWorkspacesWithOrganisations();
  }, [workspaces, searchTerm, workspaceFilterValue, datasourceFilterValue, statusFilterValue]);

  return {
    filteredWorkspaces,
    onSearchChange,
    onWorkspaceFilterChange,
    onDatasourceFilterChange,
    onStatusFilterChange,
    searchTerm,
    workspaceFilterValue,
    datasourceFilterValue,
    statusFilterValue
  };
};
